import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { HistoriesHttpRepository } from "../repository";
import { useHistoryFilters } from "./useHistoryFilters";
import type { HistoriesListParams } from "../types";
import { useDebounce } from "@/hooks/useDebounce";

const PAGE_SIZE = 10;
const historiesRepository = new HistoriesHttpRepository();

export function useHistoryList() {
  const [page, setPage] = useState(1);
  const [filters] = useHistoryFilters();

  const modelType = filters.model_type as
    | HistoriesListParams["model_type"]
    | null;
  const action = filters.action as HistoriesListParams["action"] | null;
  const ordering = filters.ordering;
  const objectPk = filters.object_pk;
  const search = filters.search;
  const debouncedSearch = useDebounce(search, 500);

  // Reset page when filters change (setState during render — React recommended pattern)
  const filterKey = `${modelType}-${action}-${ordering}-${objectPk}-${debouncedSearch}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setPage(1);
  }

  const query = useQuery({
    queryKey: [
      "histories",
      {
        page,
        model_type: modelType,
        action,
        ordering,
        object_pk: objectPk,
        search: debouncedSearch,
      },
    ],
    queryFn: () =>
      historiesRepository.fetchList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ordering: ordering ?? undefined,
        model_type: modelType ?? undefined,
        action: action ?? undefined,
        object_pk: objectPk ?? undefined,
        search: debouncedSearch ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const histories = useMemo(
    () => query.data?.results ?? [],
    [query.data?.results],
  );

  const pageCount = query.data ? Math.ceil(query.data.count / PAGE_SIZE) : 0;

  return {
    histories,
    pageCount,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    objectPk,
  };
}
